import { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Banknote } from "lucide-react";

export default function Payouts() {
  const [balances, setBalances] = useState([]);
  const [history, setHistory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/admin/payouts/balances").then((r) => setBalances(r.data));
    api.get("/admin/payouts").then((r) => setHistory(r.data));
    api.get("/admin/payouts/requests").then((r) => setRequests(r.data));
  };
  useEffect(load, []);

  const resolve = async (r, action) => {
    if (!window.confirm(`${action === "approve" ? "Approve and disburse" : "Reject"} ${r.author_name}'s withdrawal of $${r.amount.toFixed(2)}?`)) return;
    setBusy(r.id);
    try {
      await api.post(`/admin/payouts/requests/${r.id}/${action}`);
      toast.success(action === "approve" ? `$${r.amount.toFixed(2)} disbursed to ${r.author_name}` : "Request rejected — author notified");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(""); }
  };

  const disburse = async (b) => {
    const avail = b.available ?? b.outstanding;
    const claimNote = (b.pending_claims || 0) > 0 ? `\nClaimed (awaiting payout): $${b.pending_claims.toFixed(2)} — approve it from the Requests list instead.` : "";
    const input = window.prompt(
      `Disburse royalties to ${b.author_name}?\n\nAvailable balance: $${avail.toFixed(2)}${claimNote}\n\nEnter amount to disburse:`,
      avail.toFixed(2));
    if (input === null) return;
    const amount = parseFloat(input);
    if (isNaN(amount) || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(b.author_id);
    try {
      const { data } = await api.post("/admin/payouts", { author_id: b.author_id, amount });
      toast.success(`$${data.amount.toFixed(2)} disbursed to ${b.author_name} — author notified`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="payouts-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-neutral-900 font-heading">Author Payouts</h1>
        <p className="text-neutral-500 mt-1">Disburse royalty earnings to authors. Authors with a connected Stripe account receive funds automatically; otherwise transfers happen outside the platform and are recorded here.</p>
      </div>

      {requests.filter((r) => r.status === "pending").length > 0 && (
        <div className="bg-white border border-amber-200 rounded-lg overflow-hidden mb-8" data-testid="withdrawal-requests-section">
          <h3 className="font-semibold font-heading p-6 pb-2">Pending withdrawal requests</h3>
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
            <thead><tr className="bg-amber-50">
              {["Date", "Author", "Amount", "Stripe email", ""].map((h, i) => (
                <th key={i} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-neutral-100">
              {requests.filter((r) => r.status === "pending").map((r) => (
                <tr key={r.id} data-testid={`withdrawal-request-${r.id}`}>
                  <td className="px-6 py-3 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-6 py-3">
                    <p className="font-medium text-neutral-900">{r.author_name}</p>
                    <p className="text-xs text-neutral-400">{r.author_email}</p>
                  </td>
                  <td className="px-6 py-3 font-semibold text-emerald-700">${r.amount.toFixed(2)}</td>
                  <td className="px-6 py-3 text-neutral-500">{r.stripe_email || "—"}</td>
                  <td className="px-6 py-3">
                    <div className="flex gap-2">
                      <button data-testid={`approve-request-${r.id}`} onClick={() => resolve(r, "approve")} disabled={busy === r.id}
                        className="px-3 py-1.5 rounded-md text-white bg-emerald-600 hover:bg-emerald-700 text-xs font-medium disabled:opacity-40">Approve & pay</button>
                      <button data-testid={`reject-request-${r.id}`} onClick={() => resolve(r, "reject")} disabled={busy === r.id}
                        className="px-3 py-1.5 rounded-md border border-neutral-300 text-neutral-700 hover:bg-neutral-50 text-xs font-medium disabled:opacity-40">Reject</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden mb-8">
        <h3 className="font-semibold font-heading p-6 pb-2">Balances</h3>
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
          <thead><tr className="bg-neutral-50">
            {["Author", "Units", "Total earned", "Disbursed", "Claimed", "Outstanding", ""].map((h, i) => (
              <th key={i} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
          </tr></thead>
          <tbody className="divide-y divide-neutral-100">
            {balances.map((b) => (
              <tr key={b.author_id} data-testid={`balance-row-${b.author_id}`}>
                <td className="px-6 py-3">
                  <p className="font-medium text-neutral-900">{b.author_name}</p>
                  <p className="text-xs text-neutral-400">{b.author_email}</p>
                </td>
                <td className="px-6 py-3">{b.total_units}</td>
                <td className="px-6 py-3">${b.total_earned.toFixed(2)}</td>
                <td className="px-6 py-3 text-neutral-500">${b.total_disbursed.toFixed(2)}</td>
                <td className={`px-6 py-3 ${(b.pending_claims || 0) > 0 ? "text-amber-700 font-semibold" : "text-neutral-400"}`}>${(b.pending_claims || 0).toFixed(2)}</td>
                <td className={`px-6 py-3 font-semibold ${b.outstanding > 0 ? "text-emerald-700" : "text-neutral-400"}`}>${b.outstanding.toFixed(2)}</td>
                <td className="px-6 py-3">
                  <button data-testid={`disburse-button-${b.author_id}`} onClick={() => disburse(b)}
                    disabled={(b.available ?? b.outstanding) <= 0 || busy === b.author_id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-xs font-medium disabled:opacity-40">
                    <Banknote size={13} /> {busy === b.author_id ? "Disbursing..." : "Disburse"}
                  </button>
                </td>
              </tr>
            ))}
            {balances.length === 0 && <tr><td colSpan={6} className="px-6 py-6 text-neutral-400">No author earnings yet.</td></tr>}
          </tbody>
        </table></div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <h3 className="font-semibold font-heading p-6 pb-2">Payout history</h3>
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-neutral-200 text-sm text-left">
          <thead><tr className="bg-neutral-50">
            {["Date", "Author", "Amount", "Method", "Note", "Disbursed by"].map((h) => (
              <th key={h} className="px-6 py-3 font-medium text-neutral-500 uppercase tracking-wider text-xs">{h}</th>))}
          </tr></thead>
          <tbody className="divide-y divide-neutral-100">
            {history.map((p) => (
              <tr key={p.id} data-testid={`payout-history-${p.id}`}>
                <td className="px-6 py-3 whitespace-nowrap">{new Date(p.created_at).toLocaleString()}</td>
                <td className="px-6 py-3 font-medium">{p.author_name}</td>
                <td className="px-6 py-3 font-semibold text-emerald-700">${p.amount.toFixed(2)}</td>
                <td className="px-6 py-3">{p.method}</td>
                <td className="px-6 py-3 text-neutral-500">{p.note || "—"}</td>
                <td className="px-6 py-3">{p.disbursed_by_name}</td>
              </tr>
            ))}
            {history.length === 0 && <tr><td colSpan={6} className="px-6 py-6 text-neutral-400">No payouts recorded yet.</td></tr>}
          </tbody>
        </table></div>
      </div>
    </div>
  );
}
